/**
 * @fileoverview About.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import React from 'react'
import TopImageBanner from '../components/tours/TopImageBanner'
import { IMG } from '../assets/Assets'
import SEO from '../components/ui/SEO'
import { useTranslation } from 'react-i18next'

function About() {
  const { t } = useTranslation();
  return (
    <div className='flex flex-col w-full min-h-screen pt-24 md:pt-32 pb-20'>
      <SEO 
          title="About Us" 
          description="KITH Travels is a premier luxury travel agency in Sri Lanka, dedicated to curating authentic and highly personalized journeys. Discover the passionate team behind your extraordinary holiday."
          keywords="about KITH Travels, luxury travel agency Sri Lanka, bespoke travel experts Sri Lanka, personalized tours Sri Lanka, exclusive holidays"
          schema={{
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "About KITH Travels",
            "description": "KITH Travels is a premier luxury travel agency in Sri Lanka, dedicated to curating authentic and highly personalized journeys.",
            "publisher": {
              "@type": "TravelAgency",
              "name": "KITH Travels",
              "logo": window.location.origin + "/assets/logo.png",
              "url": window.location.origin
            },
            "mainEntity": {
              "@type": "TravelAgency",
              "name": "KITH Travels",
              "description": "Sri Lankan local travel agency combining deep regional knowledge with passion for hospitality.",
              "address": {
                "@type": "PostalAddress",
                "addressCountry": "Sri Lanka"
              }
            }
          }}
      />
      {/* Hero Section */}
      <div className='px-[5%] md:px-[10%]'>
        <div className='relative w-full h-[50vh] md:h-[60vh]'>
          <TopImageBanner title={t('about.hero_title')} />
        </div>
      </div>

      {/* Our Story Section */}
      <div className='px-[5%] md:px-[10%] mt-20 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center'>
        <div className='flex flex-col gap-6 text-left'>
            <h2 className='text-2xl md:text-4xl font-bold text-gray-800 leading-tight'>{t('about.our_story.title_p1')} <span className='text-KITH-primary'>{t('about.our_story.title_p2')}</span></h2>
            <p className='text-gray-600 text-sm md:text-base leading-relaxed text-justify'>
                {t('about.our_story.p1')}
            </p>
            <p className='text-gray-600 text-base leading-relaxed text-justify'>
                {t('about.our_story.p2')}
            </p>
        </div>
        <div className='w-full aspect-video rounded-[3rem] overflow-hidden shadow-2xl'>
            <img src={IMG.NuwarEliya} alt="Our Story" className='w-full h-full object-cover' loading='lazy' />
        </div>
      </div>

      {/* Core Values Section */}
      <div className='px-[5%] md:px-[10%] mt-32'>
        <div className='text-center mb-16'>
            <h2 className='text-3xl md:text-4xl font-bold text-gray-800'>{t('about.values.title')}</h2>
            <div className='w-20 h-1 bg-KITH-primary mx-auto mt-4'></div>
        </div>
        
        <div className='grid grid-cols-1 md:grid-cols-3 gap-10'>
            {[
                { icon: 'fi fi-br-shield-check', title: t('about.values.items.0.title'), desc: t('about.values.items.0.desc') },
                { icon: 'fi fi-br-heart', title: t('about.values.items.1.title'), desc: t('about.values.items.1.desc') },
                { icon: 'fi fi-br-users', title: t('about.values.items.2.title'), desc: t('about.values.items.2.desc') }
            ].map((value, idx) => (
                <div key={idx} className='flex flex-col items-center text-center p-10 bg-white rounded-[2.5rem] shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 border border-gray-50'>
                    <div className='w-20 h-20 bg-KITH-primary/10 rounded-3xl flex items-center justify-center mb-6'>
                        <i className={`${value.icon} text-KITH-primary text-3xl`}></i>
                    </div>
                    <h3 className='text-xl font-bold text-gray-800 mb-4'>{value.title}</h3>
                    <p className='text-gray-500 text-base leading-relaxed'>{value.desc}</p>
                </div>
            ))}
        </div>
      </div>

      {/* Why Choose Us */}
      <div className='px-[5%] md:px-[10%] mt-32 bg-gray-50 py-24 rounded-[4rem]'>
        <div className='max-w-4xl mx-auto text-left flex flex-col gap-12'>
            <h2 className='text-3xl md:text-4xl font-bold text-gray-800 text-center'>{t('about.why_us.title')}</h2>
            <p className='text-gray-700 text-lg leading-relaxed text-center max-w-3xl mx-auto'>
                {t('about.why_us.desc')}
            </p>
            
            <div className='grid grid-cols-1 md:grid-cols-2 gap-12 mt-4'>
                {[
                    { title: t('about.why_us.items.0.title'), desc: t('about.why_us.items.0.desc') },
                    { title: t('about.why_us.items.1.title'), desc: t('about.why_us.items.1.desc') },
                    { title: t('about.why_us.items.2.title'), desc: t('about.why_us.items.2.desc') },
                    { title: t('about.why_us.items.3.title'), desc: t('about.why_us.items.3.desc') }
                ].map((item, idx) => (
                    <div key={idx} className='flex gap-6 items-start'>
                        <div className='w-12 h-12 bg-KITH-primary text-white rounded-full flex items-center justify-center shrink-0 font-bold'>
                            {idx + 1}
                        </div>
                        <div className='flex flex-col gap-2'>
                            <h4 className='text-lg font-bold text-gray-800'>{item.title}</h4>
                            <p className='text-gray-500 text-base leading-relaxed'>{item.desc}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
      </div>
    </div>
  )
}

export default About
